import { SampleQuantities, SampleQuantitiesRecord } from "kmmp/frontend";

type QuantityEntry<K extends string | number | symbol, V> = { key: K; value: V };
type ParentQuantityEntry<K extends string | number | symbol, V> = QuantityEntry<K, V> & { total: number };

export function getTotalQuantity(quantity: SampleQuantitiesRecord | number): number {
    return typeof(quantity) === "object"
        ? sumTotalQuantities(quantity)
        : quantity;
}

export function sumTotalQuantities(quantities: SampleQuantities | SampleQuantitiesRecord): number {
    if (isSampleQuantities(quantities))
        quantities = flatten(quantities);

    return Object.values(quantities).reduce((a, b) => a + b, 0)
}

export function flatten(quantities: SampleQuantities): SampleQuantitiesRecord {
    const add = (left?: number, right?: number) =>
        (left ?? 0) + (right ?? 0);

    return Object.values(quantities)
        .reduce<SampleQuantitiesRecord>((prev, current) => {
            return {
                Beige: add(prev.Beige, current.Beige),
                Black: add(prev.Black, current.Black),
                Honey: add(prev.Honey, current.Honey),
                Mahogany: add(prev.Mahogany, current.Mahogany),
                Vintage: add(prev.Vintage, current.Vintage),
            }
        }, {});
}

export function toObject(quantities: SampleQuantities): Required<Record<keyof SampleQuantities, number>> {
    return {
        corners: sumTotalQuantities(quantities.corners),
        frames: sumTotalQuantities(quantities.frames),
        packages: sumTotalQuantities(quantities.packages),
    }
}

export function toEntries<T extends SampleQuantities | SampleQuantitiesRecord>(
  quantities: T
): Array<
  T extends SampleQuantities
    ? ParentQuantityEntry<keyof T, SampleQuantitiesRecord>
    : QuantityEntry<keyof T, number>
> {
  const isParentType = isSampleQuantities(quantities);

  return Object.entries(quantities).map(([key, value]) => {
    const base = {
      key: key as keyof T,
      value,
    };

    return isParentType
      ? { ...base, total: sumTotalQuantities(value as SampleQuantitiesRecord) }
      : base;
  }) as any;
}

function isSampleQuantities(quantities: unknown): quantities is SampleQuantities {
    if (typeof(quantities) !== "object" || quantities === null)
        return false;

    const keys: (keyof SampleQuantities)[] = ["frames", "corners", "packages"];
    const objectKeys = Object.keys(quantities);

    return objectKeys.length === keys.length
        && objectKeys.every((key) => keys.indexOf(key as keyof SampleQuantities) >= 0);
}
